import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipboardList, ArrowLeft, FileText, Link as LinkIcon, MessageSquare, Image as ImageIcon, CheckCircle2, AlertCircle, Clock, Calendar, User, ExternalLink, History, TrendingUp, ShieldX } from 'lucide-react';
import { addSubmission, getSubmissions } from '../actions/submissionActions';
import { getStudentSubmissionForms } from '../actions/submissionFormActions';
import ImageUpload from '../components/common/ImageUpload';
import RichTextRenderer from '../components/common/RichTextRenderer';
import CollaboratorSelect from '../components/common/CollaboratorSelect';

const StudentSubmissionForms = ({ auth, submission, submissionForm, addSubmission, getSubmissions, getStudentSubmissionForms }) => {
  const navigate = useNavigate();
  const { formId: urlFormId } = useParams();
  const [selectedForm, setSelectedForm] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [submissionData, setSubmissionData] = useState({
    link: '',
    comment: '',
    achievementImages: [],
    eventDate: '',
    eventTitle: ''
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSubmissionHistory, setShowSubmissionHistory] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionJustAdded, setSubmissionJustAdded] = useState(false);
  const [collaborators, setCollaborators] = useState([]);

  const { user } = auth;
  // Make sure we get submissions as an array
  const { submissions = [], loading: submissionsLoading } = submission || {};

  // Get program submission forms from dedicated API (not from bootstrap user data)
  const programSubmissionForms = submissionForm?.submissionForms || [];

  // Load user's submissions and program forms when component mounts
  useEffect(() => {
    if (user?.id) {
      getSubmissions({ userId: user.id });
      getStudentSubmissionForms();
    }
  }, [user?.id, getSubmissions, getStudentSubmissionForms]);

  // Debug submissions data
  useEffect(() => {
    if (submissions && submissions.length > 0) {
      // Submissions loaded successfully
    }
  }, [submissions]);


  // Check if user is student and has submission forms
  useEffect(() => {
    if (!user || user.role !== 'student') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Auto-select form from URL parameter
  useEffect(() => {
    if (urlFormId && programSubmissionForms.length > 0 && !selectedForm) {
      const form = programSubmissionForms.find(f => f._id === urlFormId);
      if (form) {
        setSelectedForm(form);
        setAccessDenied(false);
      } else {
        // Form not in user's program
        setAccessDenied(true);
      }
    }
  }, [urlFormId, programSubmissionForms]);

  // Handle form selection
  const handleFormSelect = (form) => {
    setSelectedForm(form);
    // Update URL with form ID for repeatable forms
    if (form.repeatable) {
      navigate(`/submissions/${form._id}`, { replace: true });
    }
    // Clear form data for new submissions
    setSubmissionData({
      link: '',
      comment: '',
      achievementImages: [],
      eventDate: '',
      eventTitle: ''
    });
    setCollaborators([]);
  };

  // Handle back to list
  const handleBackToList = () => {
    setSelectedForm(null);
    setAccessDenied(false);
    navigate('/submissions', { replace: true });
    setSubmissionData({
      link: '',
      comment: '',
      achievementImages: [],
      eventDate: '',
      eventTitle: ''
    });
    setCollaborators([]);
    // Don't show success modal when going back
    setShowSuccessModal(false);
    setShowSubmissionHistory(false);
  };

  // Validate URL
  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Check if form can be submitted
  const canSubmitForm = (form) => {
    if (!form) return false;
    
    // Check required fields
    if (form.linkRequired && !submissionData.link.trim()) return false;
    if (submissionData.link && submissionData.link.trim() && !validateUrl(submissionData.link)) return false;
    if (form.commentRequired && !submissionData.comment.trim()) return false;
    if (form.achievementImageRequired && (!submissionData.achievementImages || submissionData.achievementImages.length === 0)) return false;
    
    // Repeatable forms require eventDate and eventTitle
    if (form.repeatable) {
      if (!submissionData.eventDate) return false;
      if (new Date(submissionData.eventDate) > new Date()) return false;
      if (!submissionData.eventTitle.trim()) return false;
    }
    
    return true;
  };

  // Handle form submission
  const handleSubmitForm = async () => {
    if (!selectedForm || !canSubmitForm(selectedForm)) {
      return;
    }

    setIsSubmitting(true);
    setSubmissionJustAdded(false);

    const submissionPayload = {
      submissionFormId: selectedForm._id,
      userId: user.id
    };

    if (submissionData.link) {
      submissionPayload.link = submissionData.link;
    }
    if (submissionData.comment) {
      submissionPayload.comment = submissionData.comment;
    }
    if (submissionData.achievementImages) {
      submissionPayload.achievementImages = submissionData.achievementImages;
    }
    // Include optional repeatable-form fields
    if (selectedForm.repeatable) {
      if (submissionData.eventDate) {
        submissionPayload.eventDate = submissionData.eventDate;
      }
      if (submissionData.eventTitle) {
        submissionPayload.eventTitle = submissionData.eventTitle;
      }
    }
    // Include collaborators
    if (collaborators.length > 0) {
      submissionPayload.collaborators = collaborators.map(u => u._id);
    }

    try {
      await addSubmission(submissionPayload);
      setShowSuccessModal(true);
      setSubmissionJustAdded(true);
      // Refresh submissions list
      getSubmissions({ userId: user.id });
      // Clear form after successful submission
      setSubmissionData({
        link: '',
        comment: '',
        achievementImages: [],
        eventDate: '',
        eventTitle: ''
      });
      setCollaborators([]);
    } catch (err) {
      // Error is already dispatched to Redux error state via the action
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get submissions for a specific form
  const getFormSubmissions = (formId) => {
    if (!submissions || !Array.isArray(submissions) || !formId) {
      return [];
    }
    
    const filtered = submissions.filter(sub => {
      // Handle both populated and non-populated submissionFormId
      const subFormId = sub.submissionFormId?._id || sub.submissionFormId;
      return subFormId === formId;
    }).sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
    
    return filtered;
  };

  // Get submission count for a form
  const getSubmissionCount = (formId) => {
    return getFormSubmissions(formId).length;
  };

  // Get latest submission status for a form
  const getLatestSubmissionStatus = (formId) => {
    const formSubmissions = getFormSubmissions(formId);
    return formSubmissions.length > 0 ? formSubmissions[0].status : null;
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle2 className="w-3 h-3" />;
      case 'rejected': return <AlertCircle className="w-3 h-3" />;
      case 'pending': return <Clock className="w-3 h-3" />;
      default: return <Clock className="w-3 h-3" />;
    }
  };

  // Handle file upload
  const handleFileUpload = (images) => {
    setSubmissionData(prev => ({ ...prev, achievementImages: images }));
  };

  // Success Modal Component
  const SuccessModal = () => {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        style={{ zIndex: 9999 }}
      >
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="mb-6">
            <div className="p-4 bg-green-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-[#054581] mb-2">Submission Successful!</h3>
            <p className="text-gray-600">Your work has been submitted and is waiting for review.</p>
          </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-blue-800 text-sm">
            <Clock className="w-4 h-4" />
            <span>Status: Pending Review</span>
          </div>
          <p className="text-blue-700 text-sm mt-1">
            You'll receive feedback once an instructor reviews your submission.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => {
              setShowSuccessModal(false);
              // Refresh submissions data when closing
              getSubmissions({ userId: user.id });
            }}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Continue
          </button>
          <button
            onClick={() => {
              setShowSuccessModal(false);
              setShowSubmissionHistory(true);
              // Force refresh submissions data before showing history
              getSubmissions({ userId: user.id });
            }}
            className="flex-1 px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors"
          >
            View History
          </button>
        </div>
      </div>
    </div>
  );
  };

  // Submission History Modal Component
  const SubmissionHistoryModal = () => {
    const formSubmissions = getFormSubmissions(selectedForm?._id);
    
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        style={{ zIndex: 9999 }}
      >
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <History className="w-6 h-6 text-[#1086a4]" />
              <h3 className="text-xl font-bold text-[#054581]">Submission History</h3>
            </div>
            <button
              onClick={() => {
                setShowSubmissionHistory(false);
                // If this was opened from the list view, clear the selected form
                if (selectedForm?._isHistoryOnly) {
                  setSelectedForm(null);
                }
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-[#054581] mb-1">{selectedForm?.title}</h4>
            <p className="text-sm text-gray-600">Total Submissions: {formSubmissions.length}</p>
            {formSubmissions.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Latest: {new Date(formSubmissions[0].submittedAt).toLocaleDateString()}
              </p>
            )}
          </div>

          <div className="overflow-y-auto max-h-[60vh]">
            {submissionsLoading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-[#1086a4] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-gray-500">Loading submissions...</p>
              </div>
            ) : formSubmissions.length === 0 ? (
              <div className="text-center py-8">
                <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No submissions yet</p>
                <p className="text-xs text-gray-400 mt-2">
                  Total submissions in state: {submissions.length}
                </p>
                <p className="text-xs text-gray-400">
                  Looking for form ID: {selectedForm?._id}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {formSubmissions.map((sub, index) => (
                  <div key={sub._id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-500">#{formSubmissions.length - index}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(sub.status)}`}>
                          {getStatusIcon(sub.status)}
                          {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                        </span>
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(sub.submittedAt).toLocaleDateString()}
                        </div>
                        <div>{new Date(sub.submittedAt).toLocaleTimeString()}</div>
                      </div>
                    </div>

                    {sub.eventTitle && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-700">Title/Topic:</span>
                        <span className="ml-2 text-sm text-gray-600">{sub.eventTitle}</span>
                      </div>
                    )}

                    {sub.eventDate && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-700">Date of Event/Post:</span>
                        <span className="ml-2 text-sm text-gray-600">{new Date(sub.eventDate).toLocaleDateString()}</span>
                      </div>
                    )}

                    {sub.link && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-700">Link:</span>
                        <a 
                          href={sub.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="ml-2 text-[#1086a4] hover:text-[#054581] text-sm flex items-center gap-1 inline-flex"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {sub.link.length > 50 ? sub.link.substring(0, 50) + '...' : sub.link}
                        </a>
                      </div>
                    )}

                    {sub.comment && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-700">Comment:</span>
                        <p className="text-sm text-gray-600 mt-1">{sub.comment}</p>
                      </div>
                    )}

                    {sub.adminComment && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-yellow-600" />
                          <span className="text-sm font-medium text-yellow-800">Instructor Feedback:</span>
                        </div>
                        <p className="text-sm text-yellow-700">{sub.adminComment}</p>
                      </div>
                    )}

                    {sub.collaborators && sub.collaborators.length > 0 && (
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                        <User className="w-3.5 h-3.5 text-[#1086a4]" />
                        <span className="text-sm font-medium text-gray-700">Collaborated with:</span>
                        <div className="flex flex-wrap gap-1.5">
                          {sub.collaborators.map(collab => (
                            <span
                              key={collab._id || collab}
                              className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                            >
                              {collab.name ? `${collab.name} ${collab.lastName || ''}`.trim() : 'Unknown'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setShowSubmissionHistory(false);
                // If this was opened from the list view, clear the selected form
                if (selectedForm?._isHistoryOnly) {
                  setSelectedForm(null);
                }
              }}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Show selected form details
  if (selectedForm && !selectedForm._isHistoryOnly) {
    return (
      <div className="min-h-screen pt-20 pb-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={handleBackToList}
              className="inline-flex items-center gap-2 text-[#1086a4] hover:text-[#054581] font-medium mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Submission Forms
            </button>
            
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-[#054581] mb-2">{selectedForm.title}</h1>
                  <p className="text-gray-600">{selectedForm.repeatable ? 'Submit your work multiple times to track your progress' : 'Submit your work for review'}</p>
                </div>
                <div className="p-4 bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg">
                  <ClipboardList className="w-8 h-8 text-purple-600" />
                </div>
              </div>
              
              {/* Assignment Description */}
              {selectedForm.description && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#054581] mb-2">Assignment Description:</h4>
                  <RichTextRenderer
                    content={selectedForm.description}
                    className="text-[#1e2d40]"
                    fontSize="14px"
                    allowInlineStyles={true}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Submission Form */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-[#1086a4]" />
                <h3 className="text-lg font-semibold text-[#054581]">Submit Your Work</h3>
              </div>

              {/* Requirements */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-[#054581] mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Requirements:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className={`flex items-center gap-2 p-2 rounded ${selectedForm.linkRequired ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                    <LinkIcon className="w-4 h-4" />
                    <span>Link {selectedForm.linkRequired ? '(Required)' : '(Optional)'}</span>
                  </div>
                  <div className={`flex items-center gap-2 p-2 rounded ${selectedForm.commentRequired ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    <MessageSquare className="w-4 h-4" />
                    <span>Comment {selectedForm.commentRequired ? '(Required)' : '(Optional)'}</span>
                  </div>
                  <div className={`flex items-center gap-2 p-2 rounded ${selectedForm.achievementImageRequired ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'}`}>
                    <ImageIcon className="w-4 h-4" />
                    <span>Image {selectedForm.achievementImageRequired ? '(Required)' : '(Optional)'}</span>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                {/* Link Input */}
                <div>
                  <label className="block text-sm font-medium text-[#054581] mb-2">
                    Project Link {selectedForm.linkRequired && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="url"
                    value={submissionData.link}
                    onChange={(e) => setSubmissionData(prev => ({ ...prev, link: e.target.value }))}
                    placeholder="https://your-project-link.com"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1086a4] focus:border-transparent ${
                      submissionData.link && submissionData.link.trim() && !validateUrl(submissionData.link) ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required={selectedForm.linkRequired}
                  />
                  {submissionData.link && submissionData.link.trim() && !validateUrl(submissionData.link) && (
                    <p className="text-red-500 text-sm mt-1">Please enter a valid URL</p>
                  )}
                </div>

                {/* Comment Input */}
                <div>
                  <label className="block text-sm font-medium text-[#054581] mb-2">
                    Description/Comment {selectedForm.commentRequired && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    value={submissionData.comment}
                    onChange={(e) => setSubmissionData(prev => ({ ...prev, comment: e.target.value }))}
                    placeholder="Describe your work, challenges faced, what you learned..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1086a4] focus:border-transparent"
                    required={selectedForm.commentRequired}
                  />
                </div>

                {/* Achievement Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-[#054581] mb-2">
                    Screenshot/Image {selectedForm.achievementImageRequired && <span className="text-red-500">*</span>}
                  </label>
                  <ImageUpload
                    value={submissionData.achievementImages}
                    onChange={handleFileUpload}
                    required={selectedForm.achievementImageRequired}
                    multiple={true}
                  />
                </div>

                {/* Add Collaborators */}
                {auth.token && (
                  <CollaboratorSelect
                    value={collaborators}
                    onChange={setCollaborators}
                    tokenConfig={{ headers: { Authorization: `Bearer ${auth.token}` } }}
                  />
                )}

                {/* Date of Event/Post and Title/Topic - only for repeatable forms */}
                {selectedForm.repeatable && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-[#054581] mb-2">
                        Date of Event/Post <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={submissionData.eventDate}
                        onChange={(e) => setSubmissionData(prev => ({ ...prev, eventDate: e.target.value }))}
                        max={new Date().toISOString().split('T')[0]}
                        required
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1086a4] focus:border-transparent ${
                          submissionData.eventDate && new Date(submissionData.eventDate) > new Date() ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {submissionData.eventDate && new Date(submissionData.eventDate) > new Date() && (
                        <p className="text-red-500 text-sm mt-1">Date must be in the past</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#054581] mb-2">
                        Title/Topic <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={submissionData.eventTitle}
                        onChange={(e) => setSubmissionData(prev => ({ ...prev, eventTitle: e.target.value }))}
                        placeholder="Enter the title or topic of your event/post"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1086a4] focus:border-transparent"
                      />
                    </div>
                  </>
                )}

                {/* Submit button */}
                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-gray-600">
                      {selectedForm.repeatable
                        ? '💡 You can submit this assignment multiple times to track your progress'
                        : '💡 Submit your work for instructor review'}
                    </p>
                    {getSubmissionCount(selectedForm._id) > 0 && (
                      <button
                        onClick={() => {
                          setShowSubmissionHistory(true);
                        }}
                        className="inline-flex items-center gap-2 text-[#1086a4] hover:text-[#054581] text-sm font-medium transition-colors"
                      >
                        <History className="w-4 h-4" />
                        View History ({getSubmissionCount(selectedForm._id)})
                      </button>
                    )}
                  </div>
                  <button
                    onClick={handleSubmitForm}
                    disabled={!canSubmitForm(selectedForm) || isSubmitting}
                    className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                      canSubmitForm(selectedForm) && !isSubmitting
                        ? 'bg-[#1086a4] hover:bg-[#054581] text-white shadow-lg hover:shadow-xl'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Submitting...
                      </div>
                    ) : (
                      'Submit Work'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Modals for Form View */}
        {showSuccessModal && <SuccessModal />}
        {showSubmissionHistory && <SubmissionHistoryModal />}
      </div>
    );
  }

  // Show list of submission forms
  return (
    <div className="min-h-screen pt-20 pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-[#1086a4] hover:text-[#054581] font-medium mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          
          <div className="text-center">
            <div className="p-4 bg-purple-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <ClipboardList className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-[#054581] mb-2">Submission Forms</h1>
            <p className="text-gray-600">Complete and submit your required assignments</p>
          </div>
        </div>

        {/* Access Denied for direct link to form not in user's program */}
        {accessDenied && (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <ShieldX className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Access Denied</h3>
            <p className="text-gray-500 mb-6">You don't have access to this submission form. It may not be part of your program.</p>
            <button
              onClick={handleBackToList}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#0c6b7a] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Submission Forms
            </button>
          </div>
        )}

        {/* Submission Forms List */}
        {!accessDenied && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {programSubmissionForms.map((form) => {
            const submissionCount = getSubmissionCount(form._id);
            const latestStatus = getLatestSubmissionStatus(form._id);

            return (
              <div
                key={form._id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md hover:border-[#1086a4] transition-all cursor-pointer group flex flex-col h-full"
                onClick={() => handleFormSelect(form)}
              >
                {/* Icon + Title */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg flex-shrink-0 group-hover:from-purple-200 group-hover:to-blue-200 transition-all">
                    <ClipboardList className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-[#054581] group-hover:text-[#1086a4] transition-colors leading-snug line-clamp-2">
                    {form.title}
                  </h3>
                </div>

                {/* Requirements row */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {form.linkRequired && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 rounded text-xs text-blue-700 font-medium">
                      <LinkIcon className="w-3 h-3" /> Link
                    </span>
                  )}
                  {form.commentRequired && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 rounded text-xs text-green-700 font-medium">
                      <MessageSquare className="w-3 h-3" /> Comment
                    </span>
                  )}
                  {form.achievementImageRequired && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 rounded text-xs text-purple-700 font-medium">
                      <ImageIcon className="w-3 h-3" /> Image
                    </span>
                  )}
                  {!form.linkRequired && !form.commentRequired && !form.achievementImageRequired && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 rounded text-xs text-gray-500 font-medium">
                      <CheckCircle2 className="w-3 h-3" /> No requirements
                    </span>
                  )}
                </div>

                {/* Spacer pushes footer to bottom */}
                <div className="flex-1" />

                {/* Stats + Button footer */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">
                      <span className="font-bold text-[#1086a4]">{submissionCount}</span> submitted
                    </span>
                    {latestStatus && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(latestStatus)}`}>
                        {getStatusIcon(latestStatus)}
                        {latestStatus.charAt(0).toUpperCase() + latestStatus.slice(1)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {submissionCount > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedForm({ ...form, _isHistoryOnly: true });
                          getSubmissions({ userId: user.id });
                          setTimeout(() => {
                            setShowSubmissionHistory(true);
                          }, 100);
                        }}
                        className="p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-500 rounded transition-colors"
                        title="View submission history"
                      >
                        <History className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button className="px-3 py-1.5 bg-[#1086a4] hover:bg-[#054581] text-white text-xs font-semibold rounded-lg transition-colors">
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}

        {programSubmissionForms.length === 0 && !accessDenied && (
          <div className="text-center py-12">
            <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-500 mb-2">No Submission Forms</h3>
            <p className="text-gray-400">There are no submission forms assigned to your program.</p>
          </div>
        )}
      </div>
      
      {/* Modals */}
      {showSuccessModal && <SuccessModal />}
      {showSubmissionHistory && <SubmissionHistoryModal />}
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  submission: state.submission,
  submissionForm: state.submissionForm
});

export default connect(mapStateToProps, { addSubmission, getSubmissions, getStudentSubmissionForms })(StudentSubmissionForms);